import { useCallback, useMemo, useState } from 'react';

import { useLocalStorageValue } from 'src/app/shared/hooks/storage';

const STORAGE_KEY = 'starterDashboardV2_sharingTools_preferredFirstCard';

const DEFAULT_ORDER = ['recommended-groups', 'ready-to-share-posts', 'social-content', 'qr-poster'];

type UseSharingToolsOrderReturn = {
	orderedKeys: string[];
	initialIndex: number;
	registerClick: (clickedKey: string) => void;
};

export function useSharingToolsOrder(hasRecommendedGroups: boolean): UseSharingToolsOrderReturn {
	const [preferredFirstCard, setPreferredFirstCard] = useLocalStorageValue(STORAGE_KEY);
	const [initialPreferredFirstCard] = useState(preferredFirstCard);

	const initialIndex = useMemo(() => {
		// If user has a preferred card, use that
		if (initialPreferredFirstCard) {
			const preferredIndex = DEFAULT_ORDER.indexOf(initialPreferredFirstCard);
			if (preferredIndex !== -1) {
				return preferredIndex;
			}
		}

		// If no recommended groups, scroll to ready-to-share-posts (index 1)
		if (!hasRecommendedGroups) {
			return 1;
		}

		// Default: start at first card
		return 0;
	}, [hasRecommendedGroups, initialPreferredFirstCard]);

	const registerClick = useCallback(
		(clickedKey: string) => {
			const currentIndex = DEFAULT_ORDER.indexOf(clickedKey);
			if (currentIndex === -1) {
				return;
			}
			const nextIndex = (currentIndex + 1) % DEFAULT_ORDER.length;
			const nextKey = DEFAULT_ORDER[nextIndex];
			setPreferredFirstCard(nextKey);
		},
		[setPreferredFirstCard],
	);

	return { orderedKeys: DEFAULT_ORDER, initialIndex, registerClick };
}
